export interface TourStep {
  target: string;
  content: string;
  disableBeacon?: boolean;
}

export interface TourDefinition {
  id: string;
  label: string;
  startPath: string;
  steps: TourStep[];
}

export const TOURS: Record<string, TourDefinition> = {
  sidebar: {
    id: 'sidebar',
    label: 'TenderWright Navigation',
    startPath: '/dashboard',
    steps: [
      { target: 'aside', content: 'Navigate TenderWright from the sidebar. All features are organised into sections: Overview, Manage, Proposals, Integrations, and Compliance.', disableBeacon: true },
      { target: '[class*="sidebar"] button[class*="Proposals"], [class*="sidebar-link"]:nth-child(1)', content: 'Create and manage proposals here. Track each bid from draft through to win or loss.' },
      { target: '[class*="sidebar"] button[class*="Projects"]', content: 'Your project portfolio — past and current work that demonstrates your experience to clients.' },
      { target: '[class*="sidebar"] button[class*="People"]', content: 'Manage your team: qualifications, experience, and specialisations. Assign them to proposals.' },
      { target: '[class*="sidebar"] button[class*="Fee"]', content: 'Calculate fees with market-standard multipliers for location, complexity, and scope.' },
      { target: '[class*="sidebar"] button[class*="Analytics"]', content: 'Track your win rate, pipeline value, and business development performance over time.' },
    ],
  },
  'new-proposal': {
    id: 'new-proposal',
    label: 'Creating a New Proposal',
    startPath: '/proposals',
    steps: [
      { target: 'button:has-text("New Proposal"), [class*="btn-primary"]', content: 'Click here to start a new proposal. You\'ll enter a title, client name, and optionally select a template.', disableBeacon: true },
      { target: '[class*="status-tab"], [class*="tab"]', content: 'Filter proposals by status: Draft, In Review, Submitted, Won, or Lost.' },
      { target: 'table tbody tr:first-child, [class*="proposal-row"]:first-child', content: 'Click any proposal row to open it in the Proposal Builder.' },
    ],
  },
  'proposal-builder': {
    id: 'proposal-builder',
    label: 'The Proposal Builder',
    startPath: '/proposals',
    steps: [
      { target: '[class*="proposal-detail"], [class*="Proposal Details"]', content: 'Left panel: Edit proposal metadata — title, client, reference number, due date, and fee summary.', disableBeacon: true },
      { target: '[class*="template-section"], [class*="Template Sections"]', content: 'Centre panel: Write content for each template section. Use AI Draft buttons to generate content.' },
      { target: '[class*="tab-bar"], [role="tablist"]', content: 'Right panel tabs: Link projects, assign people, view fees, check insights, and collaborate with your team.' },
      { target: '[class*="bottom-bar"], [class*="action-bar"]', content: 'Bottom bar: Save, duplicate, export to DOCX/PDF, publish a web link, and export fee breakdowns.' },
    ],
  },
  'fee-estimator': {
    id: 'fee-estimator',
    label: 'Fee Estimator',
    startPath: '/fee-estimator',
    steps: [
      { target: '[class*="discipline"], [class*="Disciplines"]', content: 'Select the engineering disciplines involved in this project. Each adds a standard team composition.', disableBeacon: true },
      { target: '[class*="complexity"], [class*="Complexity"]', content: 'Set complexity: Low (0.7x), Medium (1.0x), High (1.4x), or Very High (1.8x). This scales all hours.' },
      { target: '[class*="location"], [class*="Location"]', content: 'Choose location: NZ Metro (1.05x), NZ Regional (1.0x), Australia (1.25x), Pacific (1.15x), or Middle East (1.40x).' },
      { target: '[class*="scope"], [class*="Scope"]', content: 'Scope phase determines the level of service: Feasibility, Preliminary, Detailed Design, or Construction.' },
      { target: '[class*="fee-estimate"], [class*="Fee Estimate"]', content: 'The fee breakdown updates in real-time: labour by role, disbursements, margin, GST, and total.' },
    ],
  },
  'go-no-go': {
    id: 'go-no-go',
    label: 'Go/No-Go Scoring',
    startPath: '/go-no-go',
    steps: [
      { target: 'input[type="text"]:first-of-type, [class*="rfp-name"]', content: 'Enter the project/RFP name and client to identify this assessment.', disableBeacon: true },
      { target: 'input[type="range"]:first-of-type, [class*="slider"]', content: 'Score each criterion from 1-5 using the sliders. The 10 criteria are weighted to produce a 0-100 score.' },
      { target: '[class*="score-circle"], [class*="donut"]', content: 'The score circle shows your total: 80+ = Strong Go, 60-79 = Go, 40-59 = Marginal, <40 = No Go.' },
      { target: '[class*="suggested"], [class*="action"]', content: 'Suggested actions and risk mitigation strategies based on your scores.' },
    ],
  },
  analytics: {
    id: 'analytics',
    label: 'Analytics Dashboard',
    startPath: '/analytics',
    steps: [
      { target: '[class*="stat-card"]:first-child, [class*="StatCard"]', content: 'Stat cards show key metrics. Click any card to drill down into details.', disableBeacon: true },
      { target: '[class*="chart-container"]:first-child, [class*="Win Rate"]', content: 'Win Rate by Client Type — see which markets you perform best in.' },
      { target: '[class*="chart-container"]:last-child, [class*="Status"]', content: 'Proposals by Status — your pipeline breakdown with values.' },
    ],
  },
};
